// Core Types for Entertainment Marketing AI v3.0

export type MediaType = 'tv' | 'movie'

export type ModelType = 'fast' | 'pro' | 'thinking'

export type InputMode = 'existing' | 'concept'

export type CampaignType = 'consumer' | 'fyc'

export type GeoRegion = 'USCAN' | 'EMEA' | 'UK' | 'EU' | 'Japan' | 'ANZ' | 'LATAM'

export const GEO_REGIONS: { value: GeoRegion; label: string; description: string }[] = [
  { value: 'USCAN', label: 'US/CAN', description: 'United States & Canada' },
  { value: 'UK', label: 'UK', description: 'United Kingdom' },
  { value: 'EU', label: 'EU', description: 'European Union' },
  { value: 'EMEA', label: 'EMEA', description: 'Europe, Middle East & Africa' },
  { value: 'LATAM', label: 'LATAM', description: 'Latin America' },
  { value: 'ANZ', label: 'ANZ', description: 'Australia & New Zealand' },
  { value: 'Japan', label: 'Japan', description: 'Japan' },
]

export interface ShowInfo {
  title: string
  genre: string
  subGenre?: string
  network?: string // For TV
  studio?: string // For Movies
  releaseDate?: string
  cast: string[]
  creators?: string[] // For TV
  director?: string // For Movies
  summary: string
  seasons?: number // For TV
  runtime?: number // For Movies (minutes)
  rating?: string // TV-MA, PG-13, etc.
}

export interface AudienceProfile {
  primaryAge: {
    min: number
    max: number
  }
  secondaryAge?: {
    min: number
    max: number
  }
  gender: {
    male: number
    female: number
    other: number
  }
  income: {
    low: number
    middle: number
    high: number
  }
  locations: string[]
  interests: string[]
  psychographics: string[]
  mediaConsumption: {
    streaming: number
    linear: number
    social: number
    theatrical?: number // For Movies
  }
}

export interface CompetitorTitle {
  title: string
  similarity: string
  successFactors: string[]
  lessons: string[]
  performance: {
    metric: string // "Ratings" for TV, "Box Office" for Movies
    value: string
  }
}

export interface CompetitorAnalysis {
  competitors: CompetitorTitle[]
  marketGap: string
  differentiators: string[]
  positioning: string
}

export interface AdPlacement {
  platform: string
  format: string
  budget: number
  reasoning: string
  expectedReach: string
  timing: string
}

export interface SocialStrategy {
  platform: string
  content: string[]
  frequency: string
  influencerStrategy?: string
  hashtags: string[]
  budget: number
}

export interface CrossPromotion {
  partner: string
  type: string
  description: string
  budget: number
  expectedImpact: string
}

export interface StuntEvent {
  name: string
  type: string
  description: string
  location: string
  timing: string
  budget: number
  mediaValue: string
}

export interface MarketingPlan {
  adPlacements: AdPlacement[]
  socialStrategy: SocialStrategy[]
  crossPromotions: CrossPromotion[]
  stuntsEvents: StuntEvent[]
  totalBudget: number
  budgetBreakdown: {
    category: string
    amount: number
    percentage: number
  }[]
  timeline: {
    phase: string
    duration: string
    activities: string[]
  }[]
}

// Regional marketing plans - one per selected geographic region
export type RegionalMarketingPlans = {
  [region in GeoRegion]?: MarketingPlan
}

export interface KeyArtConcept {
  id: string
  name: string
  description: string
  visualElements: string[]
  colorPalette: string[]
  mood: string
  tagline: string
  generativePrompt: string
  generatedImageUrl?: string
  isGenerating?: boolean
}

// FYC (For Your Consideration) Campaign Types
export interface AwardsCategory {
  category: string
  likelihood: 'high' | 'medium' | 'low'
  reasoning: string
  competingTitles: string[]
  campaignAngle: string
  voterMessaging: string
}

export interface FYCScreening {
  name: string
  location: 'New York' | 'Los Angeles' | 'Other'
  venue: string
  date: string
  description: string
  targetAttendees: string[]
  talentAttending: string[]
  qAndA: boolean
  budget: number
}

export interface VoterOutreach {
  branch: string // e.g., "Actors Branch", "Directors Branch", "Visual Effects Branch"
  strategy: string
  touchpoints: string[]
  keyMessages: string[]
  materialsNeeded: string[]
}

export interface FYCStrategy {
  overallNarrative: string
  primaryCategories: AwardsCategory[]
  screenings: FYCScreening[]
  voterOutreach: VoterOutreach[]
  tradeAdPlacements: {
    publication: string
    format: string
    timing: string
    budget: number
    messaging: string
  }[]
  guildScreenings: {
    guild: string
    date: string
    location: string
    strategy: string
  }[]
  totalBudget: number
  budgetBreakdown: {
    category: string
    amount: number
    percentage: number
  }[]
  timeline: {
    phase: string
    duration: string
    activities: string[]
  }[]
}

export interface MarketingReport {
  id: string
  mediaType: MediaType
  inputMode: InputMode
  modelUsed: ModelType
  campaignType: CampaignType
  selectedRegions: GeoRegion[]
  showInfo: ShowInfo
  audienceProfile: AudienceProfile
  competitorAnalysis: CompetitorAnalysis
  regionalPlans: RegionalMarketingPlans
  keyArtConcepts: KeyArtConcept[]
  fycStrategy?: FYCStrategy // Only present for FYC campaigns
  simulation?: AudienceSimulation // Present if generated
  groundingUrls?: string[]
  createdAt: string
  userInput: string
}

// Audience Simulation Types
export interface AudiencePersona {
  id: string
  name: string
  age: number
  gender: string
  occupation: string
  location: string
  income: 'low' | 'middle' | 'high'
  interests: string[]
  mediaHabits: string[]
  psychographicProfile: string
  avatar: string // emoji or icon representation
}

export interface CampaignElementReaction {
  element: string
  elementType: 'ad' | 'social' | 'event' | 'promo' | 'keyart'
  resonanceScore: number // 1-10
  emotionalResponse: string
  reasoning: string
  wouldShare: boolean
  wouldEngage: boolean
}

export interface PersonaReaction {
  personaId: string
  overallResonance: number // 1-10 average across all elements
  elementReactions: CampaignElementReaction[]
  topStrengths: string[]
  topConcerns: string[]
  refinementSuggestions: string[]
  likelyToWatch: 'definitely' | 'probably' | 'maybe' | 'unlikely' | 'no'
}

export interface RankedElement {
  element: string
  elementType: string
  averageScore: number
  personaScores: { personaId: string; score: number }[]
  strengths: string[]
  weaknesses: string[]
}

export interface AudienceSimulation {
  id: string
  reportId: string
  personas: AudiencePersona[]
  reactions: PersonaReaction[]
  rankedElements: RankedElement[]
  overallScore: number
  summary: string
  topRecommendations: string[]
  createdAt: string
}

// Chat Types
export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

// Analytics Types
export interface AnalyticsEvent {
  type: 'page_view' | 'report_generated'
  timestamp: string
  metadata?: Record<string, string>
}

export interface AnalyticsData {
  date: string
  pageViews: number
  reportsGenerated: number
}

export interface RefStats {
  ref: string
  pageViews: number
  reportsGenerated: number
  cacheHits: number
  conversionRate: number
}

export interface AnalyticsSummary {
  daily: AnalyticsData[]
  byRef: RefStats[]
  totals: {
    pageViews: number
    reportsGenerated: number
    cacheHits: number
    uniqueRefs: number
  }
}

// API Response Types
export interface GenerationConfig {
  temperature?: number
  topP?: number
  topK?: number
  maxOutputTokens?: number
  responseMimeType?: string
  responseSchema?: object
  thinkingConfig?: {
    thinkingBudget: number
  }
}

// Window augmentation for runtime config
declare global {
  interface Window {
    RUNTIME_CONFIG?: {
      API_AVAILABLE?: boolean
      // Legacy keys - no longer used (API calls are proxied through server)

      GEMINI_API_KEY?: string
    }
  }
}

// Response schema for Gemini API
export const marketingReportSchema = {
  type: 'object',
  properties: {
    showInfo: {
      type: 'object',
      properties: {
        title: { type: 'string' },
        genre: { type: 'string' },
        subGenre: { type: 'string' },
        network: { type: 'string' },
        studio: { type: 'string' },
        releaseDate: { type: 'string' },
        cast: { type: 'array', items: { type: 'string' } },
        creators: { type: 'array', items: { type: 'string' } },
        director: { type: 'string' },
        summary: { type: 'string' },
        seasons: { type: 'number' },
        runtime: { type: 'number' },
        rating: { type: 'string' },
      },
      required: ['title', 'genre', 'cast', 'summary'],
    },
    audienceProfile: {
      type: 'object',
      properties: {
        primaryAge: {
          type: 'object',
          properties: {
            min: { type: 'number' },
            max: { type: 'number' },
          },
          required: ['min', 'max'],
        },
        secondaryAge: {
          type: 'object',
          properties: {
            min: { type: 'number' },
            max: { type: 'number' },
          },
        },
        gender: {
          type: 'object',
          properties: {
            male: { type: 'number' },
            female: { type: 'number' },
            other: { type: 'number' },
          },
          required: ['male', 'female', 'other'],
        },
        income: {
          type: 'object',
          properties: {
            low: { type: 'number' },
            middle: { type: 'number' },
            high: { type: 'number' },
          },
          required: ['low', 'middle', 'high'],
        },
        locations: { type: 'array', items: { type: 'string' } },
        interests: { type: 'array', items: { type: 'string' } },
        psychographics: { type: 'array', items: { type: 'string' } },
        mediaConsumption: {
          type: 'object',
          properties: {
            streaming: { type: 'number' },
            linear: { type: 'number' },
            social: { type: 'number' },
            theatrical: { type: 'number' },
          },
          required: ['streaming', 'linear', 'social'],
        },
      },
      required: ['primaryAge', 'gender', 'income', 'locations', 'interests', 'psychographics', 'mediaConsumption'],
    },
    competitorAnalysis: {
      type: 'object',
      properties: {
        competitors: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              title: { type: 'string' },
              similarity: { type: 'string' },
              successFactors: { type: 'array', items: { type: 'string' } },
              lessons: { type: 'array', items: { type: 'string' } },
              performance: {
                type: 'object',
                properties: {
                  metric: { type: 'string' },
                  value: { type: 'string' },
                },
                required: ['metric', 'value'],
              },
            },
            required: ['title', 'similarity', 'successFactors', 'lessons', 'performance'],
          },
        },
        marketGap: { type: 'string' },
        differentiators: { type: 'array', items: { type: 'string' } },
        positioning: { type: 'string' },
      },
      required: ['competitors', 'marketGap', 'differentiators', 'positioning'],
    },
    regionalPlans: {
      type: 'object',
      description: 'Marketing plans keyed by region code (USCAN, UK, EU, EMEA, LATAM, ANZ, Japan)',
      additionalProperties: {
        type: 'object',
        properties: {
          adPlacements: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                platform: { type: 'string' },
                format: { type: 'string' },
                budget: { type: 'number' },
                reasoning: { type: 'string' },
                expectedReach: { type: 'string' },
                timing: { type: 'string' },
              },
              required: ['platform', 'format', 'budget', 'reasoning', 'expectedReach', 'timing'],
            },
          },
          socialStrategy: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                platform: { type: 'string' },
                content: { type: 'array', items: { type: 'string' } },
                frequency: { type: 'string' },
                influencerStrategy: { type: 'string' },
                hashtags: { type: 'array', items: { type: 'string' } },
                budget: { type: 'number' },
              },
              required: ['platform', 'content', 'frequency', 'hashtags', 'budget'],
            },
          },
          crossPromotions: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                partner: { type: 'string' },
                type: { type: 'string' },
                description: { type: 'string' },
                budget: { type: 'number' },
                expectedImpact: { type: 'string' },
              },
              required: ['partner', 'type', 'description', 'budget', 'expectedImpact'],
            },
          },
          stuntsEvents: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                name: { type: 'string' },
                type: { type: 'string' },
                description: { type: 'string' },
                location: { type: 'string' },
                timing: { type: 'string' },
                budget: { type: 'number' },
                mediaValue: { type: 'string' },
              },
              required: ['name', 'type', 'description', 'location', 'timing', 'budget', 'mediaValue'],
            },
          },
          totalBudget: { type: 'number' },
          budgetBreakdown: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                category: { type: 'string' },
                amount: { type: 'number' },
                percentage: { type: 'number' },
              },
              required: ['category', 'amount', 'percentage'],
            },
          },
          timeline: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                phase: { type: 'string' },
                duration: { type: 'string' },
                activities: { type: 'array', items: { type: 'string' } },
              },
              required: ['phase', 'duration', 'activities'],
            },
          },
        },
        required: ['adPlacements', 'socialStrategy', 'crossPromotions', 'stuntsEvents', 'totalBudget', 'budgetBreakdown', 'timeline'],
      },
    },
    keyArtConcepts: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          name: { type: 'string' },
          description: { type: 'string' },
          visualElements: { type: 'array', items: { type: 'string' } },
          colorPalette: { type: 'array', items: { type: 'string' } },
          mood: { type: 'string' },
          tagline: { type: 'string' },
          generativePrompt: { type: 'string' },
        },
        required: ['id', 'name', 'description', 'visualElements', 'colorPalette', 'mood', 'tagline', 'generativePrompt'],
      },
    },
    fycStrategy: {
      type: 'object',
      properties: {
        overallNarrative: { type: 'string' },
        primaryCategories: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              category: { type: 'string' },
              likelihood: { type: 'string' },
              reasoning: { type: 'string' },
              competingTitles: { type: 'array', items: { type: 'string' } },
              campaignAngle: { type: 'string' },
              voterMessaging: { type: 'string' },
            },
            required: ['category', 'likelihood', 'reasoning', 'competingTitles', 'campaignAngle', 'voterMessaging'],
          },
        },
        screenings: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              location: { type: 'string' },
              venue: { type: 'string' },
              date: { type: 'string' },
              description: { type: 'string' },
              targetAttendees: { type: 'array', items: { type: 'string' } },
              talentAttending: { type: 'array', items: { type: 'string' } },
              qAndA: { type: 'boolean' },
              budget: { type: 'number' },
            },
            required: ['name', 'location', 'venue', 'date', 'description', 'targetAttendees', 'talentAttending', 'qAndA', 'budget'],
          },
        },
        voterOutreach: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              branch: { type: 'string' },
              strategy: { type: 'string' },
              touchpoints: { type: 'array', items: { type: 'string' } },
              keyMessages: { type: 'array', items: { type: 'string' } },
              materialsNeeded: { type: 'array', items: { type: 'string' } },
            },
            required: ['branch', 'strategy', 'touchpoints', 'keyMessages', 'materialsNeeded'],
          },
        },
        tradeAdPlacements: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              publication: { type: 'string' },
              format: { type: 'string' },
              timing: { type: 'string' },
              budget: { type: 'number' },
              messaging: { type: 'string' },
            },
            required: ['publication', 'format', 'timing', 'budget', 'messaging'],
          },
        },
        guildScreenings: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              guild: { type: 'string' },
              date: { type: 'string' },
              location: { type: 'string' },
              strategy: { type: 'string' },
            },
            required: ['guild', 'date', 'location', 'strategy'],
          },
        },
        totalBudget: { type: 'number' },
        budgetBreakdown: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              category: { type: 'string' },
              amount: { type: 'number' },
              percentage: { type: 'number' },
            },
            required: ['category', 'amount', 'percentage'],
          },
        },
        timeline: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              phase: { type: 'string' },
              duration: { type: 'string' },
              activities: { type: 'array', items: { type: 'string' } },
            },
            required: ['phase', 'duration', 'activities'],
          },
        },
      },
      required: ['overallNarrative', 'primaryCategories', 'screenings', 'voterOutreach', 'tradeAdPlacements', 'guildScreenings', 'totalBudget', 'budgetBreakdown', 'timeline'],
    },
  },
  required: ['showInfo', 'audienceProfile', 'competitorAnalysis', 'regionalPlans', 'keyArtConcepts'],
}
